import { makeRequest, apiClient } from '../../../../services/apiClient';

// Detect 404 or "Not Found" errors robustly
const isNotFound = (err) => {
  if (!err) return false;
  const status = err?.response?.status;
  if (status === 404) return true;
  const msg = String(err?.message || err || '');
  return /Not Found/i.test(msg) || /status code\s*404/i.test(msg) || /\b404\b/.test(msg);
};

// Derive backend origin from axios baseURL or window origin
const getBackendOrigin = () => {
  try {
    const base = apiClient?.defaults?.baseURL || '';
    if (!base) return null;
    const u = new URL(base, window?.location?.origin);
    return u.origin; // e.g., http://localhost:8000
  } catch (e) {
    return null;
  }
};

/**
 * Fetch KPI for objects with missing/invalid type labels.
 * Tries multiple endpoints including primary, alias, and simple forms.
 * @param {string|null} fileType Optional OCEL file type to analyze
 * @returns {Promise<{file_type:string,total_objects:number,objects_missing_type:number,missing_ratio:number,considered_keys?:string[]}>}
 */
export const fetchMissingObjectTypeKPI = async (fileType = null) => {
  const params = fileType ? { file_type: fileType } : {};

  // 0) Try primary endpoints quickly - KPI router is mounted at /api/kpi
  try {
    return await makeRequest('/kpi/missing-object-types', { params }, 'missing object types KPI');
  } catch (e) {
    if (!isNotFound(e)) throw e;
  }
  try {
    return await makeRequest('/missing-object-types', { params }, 'missing object types KPI (legacy)');
  } catch (e) {
    if (!isNotFound(e)) throw e;
  }

  // 1) Try simple endpoints
  let simple;
  try {
    simple = await makeRequest('/missing-object-types-simple', { params }, 'missing object types KPI (simple)');
  } catch (e3) {
    if (!isNotFound(e3)) throw e3;
    try {
      simple = await makeRequest('/analytics/missing-object-types-simple', { params }, 'missing object types KPI (simple alias)');
    } catch (e4) {
      if (!isNotFound(e4)) throw e4;

      // Broader candidate URLs for different deployments
      if (typeof window !== 'undefined' && window.location) {
        const origin = getBackendOrigin() || window.location.origin;
        const candidates = [
          // Primary
          `${origin}/missing-object-types`,
          `${origin}/api/missing-object-types`,
          `${origin}/api/v1/missing-object-types`,
          `${origin}/v1/missing-object-types`,
          `${origin}/analytics/missing-object-types`,
          `${origin}/api/analytics/missing-object-types`,
          `${origin}/api/v1/analytics/missing-object-types`,
          `${origin}/v1/analytics/missing-object-types`,
          // Simple
          `${origin}/missing-object-types-simple`,
          `${origin}/api/missing-object-types-simple`,
          `${origin}/api/v1/missing-object-types-simple`,
          `${origin}/v1/missing-object-types-simple`,
          `${origin}/analytics/missing-object-types-simple`,
          `${origin}/api/analytics/missing-object-types-simple`,
          `${origin}/api/v1/analytics/missing-object-types-simple`,
          `${origin}/v1/analytics/missing-object-types-simple`,
          // Gateway variants
          `${origin}/gateway/api/missing-object-types`,
          `${origin}/gateway/api/analytics/missing-object-types`,
          `${origin}/gateway/api/missing-object-types-simple`,
          `${origin}/gateway/api/analytics/missing-object-types-simple`,
        ];
        for (const url of candidates) {
          try {
            if (/missing-object-types(?!-simple)/.test(url)) {
              const res = await makeRequest(url, { params }, `missing object types KPI (${url})`);
              if (res && typeof res === 'object' && 'total_objects' in res) return res;
            }
          } catch (e5) {
            if (!isNotFound(e5)) throw e5;
          }
        }
        for (const url of candidates) {
          if (/missing-object-types-simple/.test(url)) {
            try {
              simple = await makeRequest(url, { params }, `missing object types KPI (simple ${url})`);
              if (simple) break;
            } catch (e6) {
              if (!isNotFound(e6)) throw e6;
            }
          }
        }
      }
    }
  }

  const invalidCount = typeof simple?.missing_object_types === 'number' ? simple.missing_object_types : 0;

  // 2) Fetch total objects to compute ratio
  let total = 0;
  try {
    const totalResp = await makeRequest('/total-objects', { params }, 'total objects for missing object types');
    total = typeof totalResp?.totalObjects === 'number' ? totalResp.totalObjects : 0;
  } catch (te) {
    if (!isNotFound(te)) throw te;
    // Additional fallbacks (mount variations)
    if (typeof window !== 'undefined' && window.location) {
      const origin = getBackendOrigin() || window.location.origin;
      const totalCandidates = [
        `${origin}/total-objects`,
        `${origin}/api/total-objects`,
        `${origin}/api/v1/total-objects`,
        `${origin}/v1/total-objects`,
        `${origin}/ocel/total-objects`,
        `${origin}/api/ocel/total-objects`,
        `${origin}/gateway/api/total-objects`,
        `${origin}/gateway/api/ocel/total-objects`,
      ];
      for (const url of totalCandidates) {
        try {
          const totalRespX = await makeRequest(url, { params }, `total objects for missing object types (${url})`);
          total = typeof totalRespX?.totalObjects === 'number' ? totalRespX.totalObjects : 0;
          if (total > 0) break;
        } catch (e7) {
          if (!isNotFound(e7)) throw e7;
        }
      }
    }
  }

  const ratio = total > 0 ? invalidCount / total : 0;
  return {
    file_type: fileType ?? 'main',
    total_objects: total,
    objects_missing_type: invalidCount,
    missing_ratio: ratio,
    considered_keys: ['ocel:type']
  };
};
